import { useId, type ChangeEvent, type ReactNode } from 'react';
import { cn } from './cn.js';

interface FieldShellProps {
  id: string;
  label: ReactNode;
  hint?: string;
  children: ReactNode;
  className?: string;
}

function FieldShell({ id, label, hint, children, className }: FieldShellProps) {
  return (
    <div className={cn('flex flex-col gap-1 text-xs', className)}>
      <label htmlFor={id} className="text-muted">{label}</label>
      {children}
      {hint && <span id={`${id}-hint`} className="text-faint">{hint}</span>}
    </div>
  );
}

const inputClass =
  'rounded-control border border-border bg-surface px-2 py-1 text-sm text-text outline-none focus:border-accent';

export interface TextFieldProps {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  className?: string;
  ariaLabel?: string;
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  className,
  ariaLabel,
}: TextFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} className={className}>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        className={inputClass}
      />
    </FieldShell>
  );
}

export interface NumberFieldProps {
  label: ReactNode;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  hint?: string;
  className?: string;
  ariaLabel?: string;
}

export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step,
  hint,
  className,
  ariaLabel,
}: NumberFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} className={className}>
      <input
        id={id}
        type="number"
        value={Number.isFinite(value) ? value : ''}
        min={min}
        max={max}
        step={step ?? 'any'}
        aria-label={ariaLabel}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const next = event.target.valueAsNumber;
          if (!Number.isNaN(next)) onChange(next);
        }}
        className={inputClass}
      />
    </FieldShell>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  label: ReactNode;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  hint?: string;
  className?: string;
  ariaLabel?: string;
}

export function SelectField({
  label,
  value,
  options,
  onChange,
  hint,
  className,
  ariaLabel,
}: SelectFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} className={className}>
      <select
        id={id}
        value={value}
        aria-label={ariaLabel}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
        className={inputClass}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

export interface CheckboxFieldProps {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
}

export function CheckboxField({ label, checked, onChange, className }: CheckboxFieldProps) {
  return (
    <label className={cn('flex items-center gap-2 text-xs text-muted', className)}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
        className="h-3.5 w-3.5 rounded-control border-border accent-accent"
      />
      {label}
    </label>
  );
}
